import {
  AlertsManagementFilterBaseColumns,
  AlertsManagementFilterModel,
  useAlertsManagementListFilter,
  useSetAlertsManagementListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { RadioButton } from 'react-native-paper';
import { array, string, object as yupObject } from 'yup';
import { Colors } from '../../themes';
import { FormContext } from '../forms';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ColumnSelection } from './columnSelection';
import { FilterType } from './filterButton';
import { filterStyles as styles } from './filterStyle';

interface AlertsManagementFilterProps {
  closeFilter: () => void;
  type: FilterType;
}

export const AlertsManagementFilter = ({
  closeFilter,
  type,
}: AlertsManagementFilterProps) => {
  const { data: alertsManagementFilter } = useAlertsManagementListFilter();

  const { mutateAsync: setAlertsManagementListFilter } =
    useSetAlertsManagementListFilter();

  const formSchema = yupObject({
    ...{ keyword: string().optional().nullable() },
    columns: array().of(string().optional().nullable()).optional().nullable(),
    status: string().optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: alertsManagementFilter?.columns,
      keyword: alertsManagementFilter?.keyword || '',
      status: alertsManagementFilter?.status || '',
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
    watch,
  } = formMethods;
  const selectedColumns = watch('columns');
  const selectedStatus = watch('status');

  const isColumnsFilter = type === FilterType.ALERTSMANAGEMENTCOLUMNS;
  const isStatusFilter = type === FilterType.ALERTSMANAGEMENTSTATUS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && {
        columns: new AlertsManagementFilterModel().columns,
      }),
      ...(isStatusFilter && { status: '' }),
    });
  }, [reset, isColumnsFilter, isStatusFilter, selectedColumns]);

  const onReset = async () => {
    const updatedFields = {
      ...(isColumnsFilter && {
        columns: new AlertsManagementFilterModel().columns,
      }),
      ...(isStatusFilter && { status: '' }),
    };

    resetForm();

    const mergedFilters = { ...alertsManagementFilter, ...updatedFields };
    await applyFilter(new AlertsManagementFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    if (!data?.keyword?.length) {
      delete data.keyword;
    }
    if (!data?.status?.length) {
      delete data.status;
    }
    await applyFilter(new AlertsManagementFilterModel(data));
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([setAlertsManagementListFilter(filterModel)]);

    closeFilter();
  };

  const canReset = () => {
    return !Object.values(getValues()).every((value) => value === '');
  };

  useEffect(() => {
    if (!alertsManagementFilter) {
      resetForm();
    }
  }, [alertsManagementFilter, resetForm]);

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={AlertsManagementFilterBaseColumns}
        i18nPrefix="alertsManagement.filter"
      ></ColumnSelection>
    );
  };

  const renderStatusFilter = () => {
    const { t } = useTranslation();
    const statusItems = [
      { label: t('alertsManagement.filter.status.all'), value: '' },
      { label: t('alertsManagement.filter.status.enabled'), value: 'ENABLED' },
      {
        label: t('alertsManagement.filter.status.disabled'),
        value: 'DISABLED',
      },
    ];

    const handleStatusChange = async (value: string) => {
      const updatedFilter = {
        ...alertsManagementFilter,
        status: value || undefined,
      };
      await setAlertsManagementListFilter(
        new AlertsManagementFilterModel(updatedFilter)
      );
      closeFilter();
    };

    return (
      <View style={radioStyles.container}>
        {statusItems.map((item, index) => (
          <Pressable
            key={item.value}
            style={[
              radioStyles.radioItem,
              index === statusItems.length - 1 && radioStyles.radioItemLast,
            ]}
            onPress={() => handleStatusChange(item.value)}
          >
            <View style={radioStyles.radioContent}>
              <RadioButton
                value={item.value}
                status={
                  (selectedStatus || '') === item.value
                    ? 'checked'
                    : 'unchecked'
                }
                onPress={() => handleStatusChange(item.value)}
              />
              <Text style={radioStyles.radioLabel}>{item.label}</Text>
            </View>
          </Pressable>
        ))}
      </View>
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      {isStatusFilter ? (
        renderStatusFilter()
      ) : (
        <View style={styles.container}>
          <View style={[styles.scrollViewContainer, { height: 416 }]}>
            <ScrollView
              contentContainerStyle={styles.scrollViewContent}
              scrollEnabled={true}
              showsVerticalScrollIndicator={true}
            >
              {isColumnsFilter && renderStandardData()}
            </ScrollView>
          </View>

          <DropdownFooter
            isDirty={isDirty}
            onSubmit={handleSubmit(onSubmit)}
            canReset={canReset}
            onReset={onReset}
          />
        </View>
      )}
    </FormContext.Provider>
  );
};

const radioStyles = StyleSheet.create({
  container: {
    backgroundColor: Colors.primaryGrayBackground,
    padding: 12,
    minWidth: 200,
    flex: 1,
    justifyContent: 'center',
  },
  radioItem: {
    height: 44,
    marginBottom: 12,
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
    paddingLeft: 12,
    paddingRight: 6,
  },
  radioItemLast: {
    marginBottom: 0,
  },
  radioContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  radioLabel: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: Colors.black,
    marginLeft: 8,
  },
});
